// src/services/api.js

import { getAccessToken } from './auth';

// API base is build-time configurable (beta vs prod) via REACT_APP_API_BASE_URL.
// Defaults to the prod endpoint so a plain `npm run build` produces the prod bundle unchanged.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
const RIVER_BASE_URL = 'https://river.amazon.com/HYD20/workflows?buildingType=tseos&q0=e36d6fc9-67ff-4ca3-bbf3-8e43fbc8e2ac&q1=4bb5d5e9-304a-4815-b7d5-251f9600c658&id=4bb5d5e9-304a-4815-b7d5-251f9600c658';
const VIEWER_URL = 'https://permissions-access-management.tse.amazon.dev/viewer';
const POLL_INTERVAL = 3000; // 3 seconds

// ════════════════════════════════════════════
// AUTH HEADERS — sent with every API call
// ════════════════════════════════════════════
const getAuthHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    // Only add auth header in production (not localhost)
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        const token = getAccessToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }
    return headers;
};

// On 401: try silent refresh first (handleAuthFailure returns truthy if it renewed the
// token); only redirect as a last resort. Saves form state before any redirect.
const handleUnauthorized = async () => {
    const { handleAuthFailure } = await import('./auth');
    return await handleAuthFailure();
};

// Wrapper: on 401, silently refresh the token and RETRY the original request once.
// Only if refresh fails does handleAuthFailure redirect (and we throw to stop the caller).
const fetchWithRetry = async (url, options) => {
    let response = await fetch(url, options);

    if (response.status === 401) {
        console.log('[API] Got 401 — attempting silent token refresh...');
        const recovered = await handleUnauthorized();
        if (recovered) {
            // Rebuild auth header with the fresh token and retry once.
            const { getAccessToken } = await import('./auth');
            const token = getAccessToken();
            const retryOpts = { ...options, headers: { ...(options.headers || {}) } };
            if (token) retryOpts.headers['Authorization'] = `Bearer ${token}`;
            response = await fetch(url, retryOpts);
            if (response.status !== 401) return response;
        }
        // Refresh failed or retry still 401 → redirect already triggered by handleAuthFailure.
        throw new Error('Session expired. Redirecting to login...');
    }

    return response;
};

// ════════════════════════════════════════════
// BATCH SIZE CONFIGURATION
// 75 users per batch ≈ safe under API Gateway 29s timeout
// Adjust if needed: 50 (safer) or 100 (faster)
// ════════════════════════════════════════════
const BATCH_SIZE = 75;

// ════════════════════════════════════════════
// HELPER: Parse user list into array
// ════════════════════════════════════════════
const parseUserList = (userList) => {
    return userList
        .split(/[,\n]+/)
        .map(u => u.trim())
        .filter(u => u.length > 0);
};

// ════════════════════════════════════════════
// HELPER: Split array into batches
// ════════════════════════════════════════════
const splitIntoBatches = (users) => {
    const batches = [];
    for (let i = 0; i < users.length; i += BATCH_SIZE) {
        batches.push(users.slice(i, i + BATCH_SIZE));
    }
    return batches;
};

// ════════════════════════════════════════════
// HELPER: Merge results from all batches
// ════════════════════════════════════════════
const mergeResults = (allResults) => {
    const merged = {
        totalUsers: 0,
        totalFound: 0,
        totalEligible: 0,
        totalIneligible: 0,
        totalNotFound: 0,
        eligibleUsers: [],
        ineligibleUsers: [],
        tableData: [],
        inactiveCount: 0,
        noBasePICount: 0,
        opsCount: 0,
        nonOpsCount: 0,
        lowJobLevelCount: 0,
        canProceed: false,
        recommendation: '',
        recommendationText: '',
        validationSummary: {}
    };

    allResults.forEach(result => {
        merged.totalUsers += (result.totalUsers || 0);
        merged.totalFound += (result.totalFound || 0);
        merged.totalEligible += (result.totalEligible || 0);
        merged.totalIneligible += (result.totalIneligible || 0);
        merged.totalNotFound += (result.totalNotFound || 0);
        merged.inactiveCount += (result.inactiveCount || 0);
        merged.noBasePICount += (result.noBasePICount || 0);
        merged.opsCount += (result.opsCount || 0);
        merged.nonOpsCount += (result.nonOpsCount || 0);
        merged.lowJobLevelCount += (result.lowJobLevelCount || 0);

        if (result.eligibleUsers) {
            merged.eligibleUsers.push(...result.eligibleUsers);
        }
        if (result.ineligibleUsers) {
            merged.ineligibleUsers.push(...result.ineligibleUsers);
        }
        if (result.tableData) {
            merged.tableData.push(...result.tableData);
        }
    });

    // Calculate recommendation
    if (merged.totalEligible === merged.totalUsers && merged.totalUsers > 0) {
        merged.recommendation = 'FULL_APPROVAL';
        merged.recommendationText = '✅ All users are eligible for UFS access';
    } else if (merged.totalEligible > 0) {
        merged.recommendation = 'PARTIAL_APPROVAL';
        merged.recommendationText = `⚠️ ${merged.totalEligible} of ${merged.totalUsers} users are eligible for UFS access`;
    } else {
        merged.recommendation = 'REJECT';
        merged.recommendationText = '❌ No users are eligible for UFS access';
    }

    merged.canProceed = merged.totalEligible > 0;

    merged.validationSummary = {
        totalUsers: merged.totalUsers,
        totalEligible: merged.totalEligible,
        totalIneligible: merged.totalIneligible,
        totalNotFound: merged.totalNotFound
    };

    return merged;
};

// ════════════════════════════════════════════
// Validate users API call (single batch - direct)
// ════════════════════════════════════════════
export const validateUsers = async (payload) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'validateUsers',
            ...payload
        })
    });
    
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// ════════════════════════════════════════════
// CARDS-authoritative leader existence + level check (backend op 'checkLeader').
// Returns the raw backend payload: { alias, exists, jobLevel, cardsUnavailable, source }.
// ════════════════════════════════════════════
export const checkLeader = async (alias) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'checkLeader', alias })
    });
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// ════════════════════════════════════════════
// Validate a MANUALLY-ENTERED leader against the required level.
// CARDS is now authoritative for EXISTENCE: if CARDS is up and has no such person, that
// is a HARD BLOCK (status 'not_in_system') — previously this was a soft warn because the
// only source was the stale latest.csv. We only fall back to the old soft 'not_found'
// behavior when CARDS is UNAVAILABLE (so a transient CARDS outage never blocks a valid leader).
// Pass requesterAlias to also hard-block a requester from selecting themselves.
// Returns one of:
//   { status: 'self' }                                  // leader == requester        → HARD BLOCK
//   { status: 'ok',           found: true,  jobLevel: N }// exists + level >= required
//   { status: 'too_low',      found: true,  jobLevel: N }// exists + level < required  → HARD BLOCK
//   { status: 'not_in_system', found: false }           // CARDS up, no such person   → HARD BLOCK
//   { status: 'not_found',    found: false }            // CARDS unavailable, unverifiable → soft warn, allow
//   { status: 'error' }                                 // lookup failed
// ════════════════════════════════════════════
export const checkLeaderLevel = async (alias, requiredLevel, requesterAlias = '') => {
    const cleanAlias = (alias || '').trim().toLowerCase();
    const cleanRequester = (requesterAlias || '').trim().toLowerCase();
    if (!cleanAlias) return { status: 'error' };
    // Self-as-leader is not allowed (requester cannot approve their own request).
    if (cleanRequester && cleanAlias === cleanRequester) return { status: 'self' };
    try {
        const res = await checkLeader(cleanAlias);

        // CARDS unavailable (disabled/errored) → preserve prior soft behavior: allow with a
        // warning rather than hard-blocking on an outage. Use the CSV level if the backend
        // could supply one.
        if (res && res.cardsUnavailable) {
            const lvl = parseInt(String(res.jobLevel).replace(/[^0-9]/g, ''), 10);
            if (!isNaN(lvl)) {
                return { status: lvl >= requiredLevel ? 'ok' : 'too_low', found: true, jobLevel: lvl };
            }
            return { status: 'not_found', found: false }; // couldn't verify → soft warn, allow
        }

        // CARDS is authoritative here.
        if (res && res.exists === true) {
            const lvl = parseInt(String(res.jobLevel).replace(/[^0-9]/g, ''), 10);
            if (isNaN(lvl)) return { status: 'not_found', found: false }; // exists but no level → soft (rare)
            return { status: lvl >= requiredLevel ? 'ok' : 'too_low', found: true, jobLevel: lvl };
        }
        if (res && res.exists === false) {
            // CARDS is up and has NO such person → genuinely invalid alias → HARD BLOCK.
            return { status: 'not_in_system', found: false };
        }
        // Unexpected shape → treat as unverifiable (soft), don't wrongly hard-block.
        return { status: 'not_found', found: false };
    } catch (err) {
        console.error('checkLeaderLevel failed:', err);
        return { status: 'error' };
    }
};

// ════════════════════════════════════════════
// Submit validation job (returns immediately)
// ════════════════════════════════════════════
export const submitValidationJob = async (payload) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'submitJob',
            ...payload
        })
    });

    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// ════════════════════════════════════════════
// Check job status
// ════════════════════════════════════════════
export const checkJobStatus = async (jobId) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'getJobStatus',
            jobId: jobId
        })
    });

    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// ════════════════════════════════════════════
// Poll for results with automatic retry
// ════════════════════════════════════════════
export const pollForResults = (jobId, onStatusUpdate, onComplete, onError) => {
    let attempts = 0;
    const maxAttempts = 60;

    const poll = async () => {
        try {
            attempts++;
            const result = await checkJobStatus(jobId);

            if (result.status === 'completed') {
                onComplete(result);
                return;
            }

            if (result.status === 'failed') {
                onError(new Error(result.error || 'Validation failed'));
                return;
            }

            onStatusUpdate({
                status: result.status,
                progress: result.progress || 0,
                message: result.message || 'Processing...',
                attempts: attempts
            });

            if (attempts < maxAttempts) {
                setTimeout(poll, POLL_INTERVAL);
            } else {
                onError(new Error('Validation timed out. Please try again.'));
            }

        } catch (error) {
            onError(error);
        }
    };

    poll();
};

// ════════════════════════════════════════════
// NEW: Process single batch via async job
// Returns a Promise that resolves with results
// ════════════════════════════════════════════
const processBatchAsync = (batchUsers, payload, onBatchProgress) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Submit batch job
            const submitResponse = await submitValidationJob({
                userList: batchUsers.join(','),
                functionDropdown: payload.functionDropdown,
                teamLabel: payload.teamLabel,
                teamValue: payload.teamValue,
                useCase: payload.useCase,
                justification: payload.justification,
                priority: payload.priority,
                requester: payload.requester
            });

            if (!submitResponse.jobId) {
                throw new Error('Failed to submit batch job');
            }

            // Poll for this batch's results
            pollForResults(
                submitResponse.jobId,
                (statusUpdate) => {
                    if (onBatchProgress) {
                        onBatchProgress(statusUpdate);
                    }
                },
                (result) => {
                    resolve(result.results || result);
                },
                (error) => {
                    reject(error);
                }
            );

        } catch (error) {
            reject(error);
        }
    });
};

// ════════════════════════════════════════════
// NEW: Validate users with BATCH processing
// Splits users into batches, processes each via async job
// ════════════════════════════════════════════
export const validateUsersInBatches = async (payload, onProgress) => {
    const users = parseUserList(payload.userList);
    const batches = splitIntoBatches(users);
    const totalUsers = users.length;
    const totalBatches = batches.length;

    console.log(`📋 Total users: ${totalUsers}, Batches: ${totalBatches}, Batch size: ${BATCH_SIZE}`);

    // If single batch, process directly (no need to split)
    if (totalBatches === 1) {
        if (onProgress) {
            onProgress({
                status: 'processing',
                progress: 10,
                message: `Processing ${totalUsers} users...`,
                batchInfo: `Batch 1 of 1`,
                processedUsers: 0,
                totalUsers: totalUsers
            });
        }

        const result = await processBatchAsync(batches[0], payload, (statusUpdate) => {
            if (onProgress) {
                onProgress({
                    ...statusUpdate,
                    batchInfo: `Batch 1 of 1`,
                    processedUsers: 0,
                    totalUsers: totalUsers
                });
            }
        });

        if (onProgress) {
            onProgress({
                status: 'completed',
                progress: 100,
                message: 'Validation completed!',
                batchInfo: `All batches complete`,
                processedUsers: totalUsers,
                totalUsers: totalUsers
            });
        }

        return result;
    }

    // Multiple batches - process sequentially
    const allResults = [];
    let processedUsers = 0;

    for (let i = 0; i < totalBatches; i++) {
        const batch = batches[i];
        const batchNumber = i + 1;

        // Update progress
        if (onProgress) {
            const overallProgress = Math.round((i / totalBatches) * 100);
            onProgress({
                status: 'processing',
                progress: overallProgress,
                message: `Processing batch ${batchNumber} of ${totalBatches} (${batch.length} users)...`,
                batchInfo: `Batch ${batchNumber} of ${totalBatches}`,
                processedUsers: processedUsers,
                totalUsers: totalUsers
            });
        }

        console.log(`🔄 Batch ${batchNumber}/${totalBatches}: Processing ${batch.length} users`);

        try {
            const result = await processBatchAsync(batch, payload, (statusUpdate) => {
                if (onProgress) {
                    const baseProgress = Math.round((i / totalBatches) * 100);
                    const batchProgress = Math.round(((statusUpdate.progress || 0) / 100) * (100 / totalBatches));
                    onProgress({
                        status: 'processing',
                        progress: Math.min(baseProgress + batchProgress, 99),
                        message: `Batch ${batchNumber}/${totalBatches}: ${statusUpdate.message || 'Processing...'}`,
                        batchInfo: `Batch ${batchNumber} of ${totalBatches}`,
                        processedUsers: processedUsers,
                        totalUsers: totalUsers
                    });
                }
            });

            allResults.push(result);
            processedUsers += batch.length;
            console.log(`✅ Batch ${batchNumber} complete: ${batch.length} users processed`);

        } catch (error) {
            console.error(`❌ Batch ${batchNumber} failed:`, error);

            // Create error entries for failed batch
            allResults.push({
                totalUsers: batch.length,
                totalFound: 0,
                totalEligible: 0,
                totalIneligible: 0,
                totalNotFound: batch.length,
                eligibleUsers: [],
                ineligibleUsers: [],
                tableData: batch.map(login => ({
                    login: login,
                    inactivity: 'Error',
                    inactiveDays: 'N/A',
                    basePIAccess: 'N/A',
                    jobFamily: 'N/A',
                    jobLevel: 'N/A',
                    jobLevelStatus: 'N/A',
                    eligible: `Error - Batch ${batchNumber} failed`
                })),
                inactiveCount: 0,
                noBasePICount: 0,
                opsCount: 0,
                nonOpsCount: 0,
                lowJobLevelCount: 0
            });
            processedUsers += batch.length;
        }
    }

    // Final progress
    if (onProgress) {
        onProgress({
            status: 'merging',
            progress: 99,
            message: 'All batches complete! Merging results...',
            batchInfo: 'Merging all batches',
            processedUsers: totalUsers,
            totalUsers: totalUsers
        });
    }

    // Merge all batch results
    const merged = mergeResults(allResults);
    console.log(`🎉 All done! ${merged.totalEligible}/${merged.totalUsers} eligible`);

    if (onProgress) {
        onProgress({
            status: 'completed',
            progress: 100,
            message: 'Validation completed!',
            batchInfo: 'Complete',
            processedUsers: totalUsers,
            totalUsers: totalUsers
        });
    }

    return merged;
};

// ════════════════════════════════════════════
// T321 ELIGIBILITY (PML) — batched, with progress. Reuses the SAME async job transport
// as T178 (submitJob → self-invoke processJob → poll getJobStatus), passing
// validator:'validatePML' + permissions. Merges the PML-shaped per-batch results
// (results[]/summary/basePiPending) into one object. onProgress gets
// {status, progress, message, batchInfo, processedUsers, totalUsers}.
// ════════════════════════════════════════════
const mergePmlResults = (allResults) => {
    const merged = {
        success: true, results: [], basePiPending: [],
        summary: { total: 0, passed: 0, blocked: 0, basePiPending: 0 },
        canProceed: true, trainingRequired: false,
        basePiBindleUrl: '', trainingRequestUrl: '', dataSource: null,
    };
    allResults.forEach(r => {
        if (!r) return;
        if (Array.isArray(r.results)) merged.results.push(...r.results);
        if (Array.isArray(r.basePiPending)) merged.basePiPending.push(...r.basePiPending);
        if (r.summary) {
            merged.summary.total += (r.summary.total || 0);
            merged.summary.passed += (r.summary.passed || 0);
            merged.summary.blocked += (r.summary.blocked || 0);
            merged.summary.basePiPending += (r.summary.basePiPending || 0);
        }
        if (r.trainingRequired) merged.trainingRequired = true;
        if (r.canProceed === false) merged.canProceed = false;
        // Keep the first non-empty audit / link values (same file/links across batches).
        if (r.dataSource && !merged.dataSource) merged.dataSource = r.dataSource;
        if (r.basePiBindleUrl && !merged.basePiBindleUrl) merged.basePiBindleUrl = r.basePiBindleUrl;
        if (r.trainingRequestUrl && !merged.trainingRequestUrl) merged.trainingRequestUrl = r.trainingRequestUrl;
    });
    return merged;
};

export const validatePMLInBatches = async (userList, permissions, onProgress) => {
    const users = parseUserList(userList);
    const batches = splitIntoBatches(users);
    const totalUsers = users.length;
    const totalBatches = batches.length;

    const submitPmlBatch = (batchUsers, batchNo) => new Promise(async (resolve, reject) => {
        try {
            const sub = await submitValidationJob({
                userList: batchUsers.join(','),
                validator: 'validatePML',
                permissions: permissions,
            });
            if (!sub.jobId) throw new Error('Failed to submit eligibility batch');
            pollForResults(
                sub.jobId,
                (st) => { if (onProgress) onProgress({
                    status: 'processing',
                    progress: Math.min(Math.round(((batchNo - 1) / totalBatches) * 100) + Math.round((st.progress || 0) / totalBatches), 99),
                    message: `Batch ${batchNo}/${totalBatches}: ${st.message || 'validating...'}`,
                    batchInfo: `Batch ${batchNo} of ${totalBatches}`,
                    processedUsers: (batchNo - 1) * BATCH_SIZE, totalUsers,
                }); },
                (result) => resolve(result.results || result),
                (err) => reject(err),
            );
        } catch (e) { reject(e); }
    });

    const allResults = [];
    let processed = 0;
    for (let i = 0; i < totalBatches; i++) {
        const batchNo = i + 1;
        if (onProgress) onProgress({
            status: 'processing', progress: Math.round((i / totalBatches) * 100),
            message: `Processing batch ${batchNo} of ${totalBatches} (${batches[i].length} users)...`,
            batchInfo: `Batch ${batchNo} of ${totalBatches}`, processedUsers: processed, totalUsers,
        });
        const res = await submitPmlBatch(batches[i], batchNo);
        allResults.push(res);
        processed += batches[i].length;
    }
    if (onProgress) onProgress({ status: 'completed', progress: 100, message: 'Eligibility check complete.',
        batchInfo: 'Complete', processedUsers: totalUsers, totalUsers });
    return mergePmlResults(allResults);
};

// ════════════════════════════════════════════
// Store results and get request ID
// ════════════════════════════════════════════
export const storeResults = async (dataToStore) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'storeResults',
            ...dataToStore
        })
    });
    
    const result = await response.json();
    return typeof result.body === 'string' ? JSON.parse(result.body) : result;
};


// ════════════════════════════════════════════
// 🆕 NEW: Submit to Approvals.amazon.com
// Creates approval via Lambda → AppSync
// Used for "Unlisted Use Case" requests
// ════════════════════════════════════════════
export const submitToApprovals = async (approvalData) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'createApproval',
            requestId: approvalData.requestId,
            referenceId: approvalData.requestId,
            requesterAlias: approvalData.requesterAlias,
            l8ManagerAlias: approvalData.l8ManagerAlias || '',
            templateId: approvalData.templateId || '',
            title: approvalData.title,
            summary: approvalData.summary,
            viewerUrl: approvalData.viewerUrl,
            formData: approvalData.formData,
            validationSummary: approvalData.validationSummary
        })
    });

    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;

    if (parsed.error) {
        throw new Error(parsed.error);
    }

    return parsed;
};

// ════════════════════════════════════════════
// Get validation results by request ID (for Viewer page)
// ════════════════════════════════════════════
export const getValidationResults = async (requestId) => {
    const response = await fetch(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'getResults',
            requestId: requestId
        })
    });

    const result = await response.json();

    if (result.statusCode === 404) {
        throw new Error('Request not found');
    }

    let data = typeof result.body === 'string' ? JSON.parse(result.body) : result;

    if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
    }

    return data;
};

// ════════════════════════════════════════════
// Build RIVER URL (with smart user list handling)
// ════════════════════════════════════════════
export const buildRiverUrl = (requestId, formData, validationResults, userList) => {
    const viewerLink = `${VIEWER_URL}?id=${encodeURIComponent(requestId)}`;

    let riverURL = RIVER_BASE_URL;

    // Request ID and Viewer Link
    riverURL += `&requestId=${encodeURIComponent(requestId)}`;
    riverURL += `&validationResultsLink=${encodeURIComponent(viewerLink)}`;

    // Basic form fields
    riverURL += `&functionDropdown=${encodeURIComponent(formData.functionDropdown || '')}`;
    riverURL += `&team=${encodeURIComponent(formData.teamLabel || '')}`;
    riverURL += `&teamValue=${encodeURIComponent(formData.teamValue || '')}`;
    riverURL += `&useCase=${encodeURIComponent(formData.useCase || '')}`;
    riverURL += `&justification=${encodeURIComponent(formData.justification || '')}`;
    riverURL += `&priority=${encodeURIComponent(formData.priority || '')}`;

    // ════════════════════════════════════════════
    // 🆕 New fields
    // ════════════════════════════════════════════
    riverURL += `&orgName=${encodeURIComponent(formData.orgName || '')}`;
    if (formData.customTeamName) {
        riverURL += `&customTeamName=${encodeURIComponent(formData.customTeamName)}`;
    }
    riverURL += `&userLevels=${encodeURIComponent((formData.userLevels || []).join(', '))}`;
    if (formData.queues) {
        riverURL += `&queues=${encodeURIComponent(formData.queues)}`;
    }
    riverURL += `&frequency=${encodeURIComponent((formData.frequency || []).join(', '))}`;
    riverURL += `&searchCriteria=${encodeURIComponent((formData.searchCriteria || []).join(', '))}`;

    // Validation summary
    riverURL += `&totalUsers=${validationResults.totalUsers || 0}`;
    riverURL += `&totalEligible=${validationResults.totalEligible || 0}`;
    riverURL += `&totalIneligible=${validationResults.totalIneligible || 0}`;
    riverURL += `&totalNotFound=${validationResults.totalNotFound || 0}`;
    riverURL += `&recommendation=${encodeURIComponent(validationResults.recommendation || '')}`;
    riverURL += `&recommendationText=${encodeURIComponent(validationResults.recommendationText || '')}`;

    // Smart user list handling
    const totalUsers = validationResults.totalUsers || 0;

    if (totalUsers < 20 && userList) {
        riverURL += `&userList=${encodeURIComponent(userList)}`;
    } else {
        riverURL += `&userList=${encodeURIComponent(
            `⚠️ ${totalUsers} users submitted - User list is too long to display here. ` +
            `Please click the Validation Results Link above to view the complete user list and detailed results.`
        )}`;
    }

    // Eligible users (only if fits)
    const eligibleShort = (validationResults.eligibleUsers || []).join(',');
    if (eligibleShort.length < 1000) {
        riverURL += `&eligibleUsers=${encodeURIComponent(eligibleShort)}`;
    }

    // Notes with viewer link
    const notes = `📊 View Full Validation Results: ${viewerLink} | Request ID: ${requestId} | Total: ${validationResults.totalUsers} | Eligible: ${validationResults.totalEligible} | ${validationResults.recommendation}`;
    riverURL += `&notes=${encodeURIComponent(notes)}`;
    riverURL += `&submittedFrom=UFS_Portal`;

    // Safety check - if URL too long, use minimal version
    if (riverURL.length > 8000) {
        console.warn(`⚠️ URL too long (${riverURL.length} chars), using minimal version`);

        riverURL = RIVER_BASE_URL;
        riverURL += `&requestId=${encodeURIComponent(requestId)}`;
        riverURL += `&validationResultsLink=${encodeURIComponent(viewerLink)}`;
        riverURL += `&functionDropdown=${encodeURIComponent(formData.functionDropdown || '')}`;
        riverURL += `&team=${encodeURIComponent(formData.teamLabel || '')}`;
        riverURL += `&orgName=${encodeURIComponent(formData.orgName || '')}`;
        riverURL += `&priority=${encodeURIComponent(formData.priority || '')}`;
        riverURL += `&totalUsers=${validationResults.totalUsers || 0}`;
        riverURL += `&totalEligible=${validationResults.totalEligible || 0}`;
        riverURL += `&recommendation=${encodeURIComponent(validationResults.recommendation || '')}`;
        riverURL += `&userList=${encodeURIComponent(
            `⚠️ ${totalUsers} users submitted - Please click the Validation Results Link to view the complete list.`
        )}`;
        riverURL += `&notes=${encodeURIComponent(`📊 View Full Results: ${viewerLink}`)}`;
    }

    console.log(`📏 RIVER URL length: ${riverURL.length} characters | Users: ${totalUsers} | UserList included: ${totalUsers < 200}`);

    return riverURL;
};

export { VIEWER_URL };

// ════════════════════════════════════════════
// APPROVER API — Phase 2
// ════════════════════════════════════════════

const APPROVER_ENDPOINT = `${API_BASE_URL}/approver-api`;

// Get dashboard stats
export const getApproverDashboard = async () => {
    const response = await fetch(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'getDashboard' })
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// List requests by status
export const listRequests = async (status = 'PENDING', lastKey = null) => {
    const response = await fetch(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'listRequests', status, lastKey })
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// List all closed requests (approved + rejected)
export const listClosedRequests = async (lastKey = null) => {
    const response = await fetch(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'listClosed', lastKey })
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// Get full request details
export const getRequestDetails = async (requestId) => {
    const response = await fetch(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'getRequest', requestId })
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// Update request status (approve/reject)
export const updateRequestStatus = async (requestId, newStatus, approver, notes = '') => {
    const response = await fetch(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'updateStatus',
            requestId,
            newStatus,
            approver,
            notes
        })
    });

    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (!response.ok || parsed.error) {
        throw new Error(parsed.error || `API error: ${response.status}`);
    }
    return parsed;
};

// ════════════════════════════════════════════
// Get all requests for a specific user alias
// ════════════════════════════════════════════
export const getMyRequests = async (alias) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'getMyRequests',
            alias: alias
        })
    });

    const result = await response.json();
    const parsed = typeof result.body === 'string' ? JSON.parse(result.body) : result;

    if (parsed.error) {
        throw new Error(parsed.error);
    }

    return parsed;
};

// ════════════════════════════════════════════
// PERMISSION Chat Assistant — UI Mapping
// ════════════════════════════════════════════

const UI_MAPPING_ENDPOINT = `${API_BASE_URL}/ui-mapping`;

// Search for permissions by query and/or screenshot
export const searchPermissions = async (query, imageBase64 = null, mediaType = 'image/png') => {
    const payload = {
        operation: 'search',
        query: query || ''
    };

    if (imageBase64) {
        payload.imageBase64 = imageBase64;
        payload.mediaType = mediaType;
    }

    const response = await fetch(UI_MAPPING_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error(`Permission Chat Assistant error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// Chat with permission Chat Assistant//
export const permissionChat = async (message, history = []) => {
    const response = await fetch(UI_MAPPING_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'chat',
            message,
            history
        })
    });

    if (!response.ok) throw new Error(`Permission Chat Assistant error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// List all permissions
export const listAllPermissions = async () => {
    const response = await fetch(UI_MAPPING_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'listPermissions' })
    });

    if (!response.ok) throw new Error(`Permission Chat Assistant error: ${response.status}`);
    const data = await response.json();
    return typeof data.body === 'string' ? JSON.parse(data.body) : data;
};

// 🆕 Look up L8+ manager for a given alias
export const lookupL8Manager = async (alias) => {
    const response = await fetch(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'lookupL8Manager',
            alias: alias
        })
    });
    // Surface auth failures distinctly so callers can trigger re-auth instead of
    // silently treating a 401 as "no department" (which showed the wrong "no Pre-Approved" screen).
    if (response.status === 401 || response.status === 403) {
        const err = new Error('AUTH_EXPIRED');
        err.isAuthError = true;
        throw err;
    }
    const result = await response.json();
    return typeof result.body === 'string' ? JSON.parse(result.body) : result;
};

// T321 department detection via CARDS (costCenterName) — the same value shown as
// "Department" on permissions.amazon.com, populated for every employee (no latest.csv gap).
// Returns { success, userDepartment, userJobFamily, source }. Falls back to CSV server-side
// if CARDS is unavailable. Used by the T321 flow; T178 still uses lookupL8Manager.
export const lookupDepartmentCards = async (alias) => {
    const response = await fetch(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'lookupDepartmentCards',
            alias: alias
        })
    });
    if (response.status === 401 || response.status === 403) {
        const err = new Error('AUTH_EXPIRED');
        err.isAuthError = true;
        throw err;
    }
    const result = await response.json();
    return typeof result.body === 'string' ? JSON.parse(result.body) : result;
};


// ════════════════════════════════════════════
// Team Config — fetch from DynamoDB via approver-api
// ════════════════════════════════════════════
export const getTeamConfig = async (teamLabel) => {
    const response = await fetchWithRetry(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'getTeamConfig', teamLabel })
    });
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

// ════════════════════════════════════════════
// Lookup Manager by target level (L6/L7/L8)
// ════════════════════════════════════════════
export const lookupManager = async (alias, targetLevel = 8) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'lookupL8Manager',
            alias,
            targetLevel
        })
    });

    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

export const listTeamConfigs = async () => {
    const response = await fetchWithRetry(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'listTeamConfigs' })
    });
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

// ════════════════════════════════════════════
// T321 Team Configs
// ════════════════════════════════════════════
export const listT321TeamConfigs = async () => {
    const response = await fetchWithRetry(APPROVER_ENDPOINT, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'listT321TeamConfigs' })
    });
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

// ════════════════════════════════════════════
// T321 User Validation (Active + Base Access)
// ════════════════════════════════════════════
export const validateT321Users = async (userList, checkBaseAccess = false) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'validateT321Users', userList, checkBaseAccess })
    });
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

// ════════════════════════════════════════════
// T321 L7 Leader lookup (CARDS managementChain, CSV fallback)
// Returns { success, leaderAlias, leaderJobLevel, chain, managerChain, source }
// ════════════════════════════════════════════
export const lookupT321Leader = async (alias, targetLevel = 7) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ operation: 'lookupT321Leader', alias, targetLevel })
    });
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

// ════════════════════════════════════════════
// T321 Approval submission
// pre-approved -> 1 stage CTPS (template 1013154); unlisted Base -> L7->CTPS;
// unlisted Regulated/NOIR -> CTPS only (template 1013155 + plan)
// ════════════════════════════════════════════
export const submitT321Approval = async (approvalData) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'createT321Approval',
            requestId: approvalData.requestId,
            referenceId: approvalData.requestId,
            requesterAlias: approvalData.requesterAlias,
            useCaseType: approvalData.useCaseType,          // 'approved' | 'unlisted'
            pathId: approvalData.pathId,                    // 'base' | 'regulated' | 'noir'
            l7ManagerAlias: approvalData.l7ManagerAlias || '',
            title: approvalData.title,
            summary: approvalData.summary,
            viewerUrl: approvalData.viewerUrl,
            formData: approvalData.formData,
            validationSummary: approvalData.validationSummary
        })
    });
    const data = await response.json();
    const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

// Cut ONE separate Base PI approval for the union of users missing Base PI across the
// submitted categories. unlisted → L7→CTPS (needs l7ManagerAlias); approved → CTPS-only.
export const submitT321BasePiApproval = async (data) => {
    const response = await fetchWithRetry(`${API_BASE_URL}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            operation: 'createT321BasePiApproval',
            requesterAlias: data.requesterAlias,
            useCaseType: data.useCaseType,
            l7ManagerAlias: data.l7ManagerAlias || '',
            pendingUsers: data.pendingUsers || [],
            parentRequestIds: data.parentRequestIds || [],
            teamLabel: data.teamLabel || '',
            viewerUrl: data.viewerUrl || '',
            businessReason: data.businessReason || '',
        })
    });
    const d = await response.json();
    const parsed = typeof d.body === 'string' ? JSON.parse(d.body) : d;
    if (parsed.error) throw new Error(parsed.error);
    return parsed;
};

